import {
	BasicFilterExpression,
	CompoundFilterExpression,
	DataOptions,
	DataResponse,
	extractDynamicSelectionExpressions,
	FilterDynamicSelectionExpression,
	FilterExpression,
} from '../../../services/data.service';
import { Observable } from 'rxjs';
import { MultiAggregator, SingleAggregator, sumOver } from '../../../services/aggregation';
import { Level, Path, pathTo } from '../../../services/data-tree';
import { DashboardContext, DataRequestContext } from './dashboard-context';
import { BarInfo } from '../../../services/stacked-bars';
import { Dashboard } from './dashboard';
import { inject, signal, WritableSignal } from '@angular/core';
import { QueryParamStateService } from '../../../services/query-param-state.service';
import { Att, AttrPath, Table } from '@cumlaude/metadata';
import { count_records } from '../../../services/measures';
import { AggregatorConfig } from './aggregator-config';
import { DashboardVariant } from '../../../services/weergave-opties';

export interface Attributes {
	count_records: number;
}

export type GroupValue = {
	name: Att;
	value: string | null;
};

export abstract class BaseDashboardConfig<I extends Attributes, A extends Attributes> extends Dashboard implements AggregatorConfig<I, A> {
	protected qp = inject(QueryParamStateService);

	readonly varianten: DashboardVariant[] = Object.values(DashboardVariant);

	/**
	 * Deze overriden in je subclass. Geeft meer type safety dan getSingleAggregators() overriden zou geven.
	 */
	protected singleAggregators: Partial<{ [ai in keyof A]: SingleAggregator<I, A[ai]> }> = {};

	/**
	 * Deze overriden in je subclass. Geeft meer type safety dan getMultiAggregators() overriden zou geven.
	 */
	protected multiAggregators: MultiAggregator<keyof A, I, A, A[keyof A]>[] = [];

	readonly dataRootSelection: WritableSignal<Selection | undefined> = signal(undefined);

	abstract getData(options: DataOptions): Observable<DataResponse<number[]>>;

	/**
	 * Methode om optioneel extra afhandeling te doen met de dashboard data.
	 */
	onContextCreated(_context: DashboardContext<I, A, BaseDashboardConfig<I, A>>) {
		//Implementatie in child
	}

	getSingleAggregators(): Partial<{ [ai in keyof A]: SingleAggregator<I, A[ai]> }> {
		const defaults = <Partial<{ [ai in keyof A]: SingleAggregator<I, A[ai]> }>>{ count_records: sumOver('count_records') };
		return { ...defaults, ...this.singleAggregators };
	}

	getMultiAggregators(): MultiAggregator<keyof A, I, A, A[keyof A]>[] {
		const defaults: MultiAggregator<keyof A, I, A, A[keyof A]>[] = [];
		return [...defaults, ...this.multiAggregators];
	}

	/**
	 * Organiseert de data voor een rij als "partition", dwz een verzameling stacks waarbij elke stack een verzameling bars is.
	 */
	partitionBarData(rowRoot: Level<A, number[]>, _context: DashboardContext<I, A, BaseDashboardConfig<I, A>>): Path<A, number[]>[][] {
		return [rowRoot.r];
	}

	/**
	 * Bepaalt de presentatie-attributen voor een enkele bar.
	 */
	makeBar(attrs: I, path: Path<A, number[]>, context: DashboardContext<I, A, BaseDashboardConfig<I, A>>): BarInfo {
		return {
			size: attrs.count_records,
			selection: this.createSelection(context, path),
			className: 'dashboard-default',
		};
	}

	getSelectionConfig(_context: DataRequestContext<A>): SelectionConfig<A> | undefined {
		const map: Partial<{ [t in Table]: [string, string] }> = {
			fac_ds_doorstroom: ['doorstroom', '/details/leerling/doorstroom'],
			fac_cf_cijfer: ['cijfers', '/details/leerling/cijferlijst'],
			fac_aw_aanwezigheid: ['aanwezigheid', '/details/leerling/afwezigheid'],
			fac_bv_basisvaardigheden: ['basisvaardigheden', '/details/leerling/basisvaardigheden'],
			fac_lr_lesregistratie: ['lesregistratie', '/details/leerling/lesregistraties'],
			fac_vkk_vakkeuze: ['vakkeuze', '/details/leerling/vakken'],
		};
		const [provider, detailUrl] = map[this.factTable] ?? [];
		if (provider === undefined || detailUrl === undefined) return undefined;

		return { detailUrl, mergeQueryParams: false, provider, getSize: count_records, extraFilters: [] };
	}

	isSelectionAllowed(context: DashboardContext<I, A, BaseDashboardConfig<I, A>>) {
		const selectionConfig = this.getSelectionConfig(context);
		if (!selectionConfig) return false;
		return this.autorisatieService.isUrlAllowed(selectionConfig.detailUrl);
	}

	createSelection(
		context: DashboardContext<I, A, BaseDashboardConfig<I, A>>,
		path: Path<A, number[]>,
		overrideConfig: Partial<SelectionConfig<A>> = {}
	): Selection | undefined {
		if (!this.isSelectionAllowed(context)) return undefined;

		const dashboardConfig = this.getSelectionConfig(context)!;
		return createSelection(context, path, { ...dashboardConfig, ...overrideConfig });
	}

	createDataRootSelection(
		context: DashboardContext<I, A, BaseDashboardConfig<I, A>> & {
			dataRoot: NonNullable<DashboardContext<I, A, BaseDashboardConfig<I, A>>['dataRoot']>;
		},
		overrideConfig: Partial<SelectionConfig<A>> = {}
	) {
		return this.createSelection(context, pathTo(context.dataRoot), overrideConfig);
	}
}

export type SelectionConfig<A> = {
	detailUrl: string;
	mergeQueryParams: boolean;
	provider: string;
	getSize: (path: Path<A, number[]>) => number;
	extraFilters: FilterExpression[];
	/**
	 * Default true. Zet op false als de selectie niet als leerlingselectie wordt gebruikt (bijv. docent): een geneste
	 * selectie van een andere provider blijft dan een gewoon filter binnen de nieuwe selectie, in plaats van ernaast te komen.
	 */
	moveNestedSelectionsUp?: boolean;
};

export type Selection = { detailUrl: string; mergeQueryParams: boolean; expression: FilterExpression; size: number };

/**
 * NB bestaande leerlingselecties binnen de filters worden omhoog getrokken naar de nieuwe selectie, tenzij
 * `properties.moveNestedSelectionsUp` false is.
 */
export function createSelection<A extends Attributes>(
	context: DataRequestContext<A>,
	path: Path<A, number[]>,
	properties: SelectionConfig<A>
): Selection {
	const levelFilters = getLevelFilters(context, path);
	const { detailUrl, mergeQueryParams, provider, getSize, extraFilters, moveNestedSelectionsUp = true } = properties;
	const [existingDynamic, contextFilter] = moveNestedSelectionsUp ? extractDynamicSelectionExpressions(context.filter) : [[], context.filter];
	const sf = new CompoundFilterExpression([...(contextFilter ? [contextFilter] : []), ...levelFilters, ...extraFilters]);
	const newDynamic = new FilterDynamicSelectionExpression(provider, sf);
	const expression = existingDynamic.length === 0 ? newDynamic : new CompoundFilterExpression([newDynamic, ...existingDynamic]);
	const size = getSize(path);
	return { detailUrl, mergeQueryParams, expression, size };
}

export function getLevelFilters<A extends Attributes>(context: DataRequestContext<A>, path: Level<unknown, number[]>[]) {
	const levelNames = [...context.groupNames, ...context.subgroupNames];
	return path.slice(1).map((level, ix) => new BasicFilterExpression(levelNames[ix].split('.') as AttrPath, level.k));
}
